import { FormikTouched } from "formik";
import _debounce from "lodash/debounce";
import React, { cloneElement, createRef, forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Group } from "reakit/Group";
import { Rover, RoverProps } from "reakit/Rover";
import styled, { css } from "styled-components";
import { DropdownHandler } from "./DropdownHandler";
import { useKeyboardNav } from "./hooks/useKeyboardNav";
import { CoordData, usePortalDropdownCoords } from "./hooks/usePortalDropdownCoords";
import { Icon, IconSvg } from "./Icon";
import { ListItem } from "./ListItem";
import { mediaQueries, style } from "./style";
import { RequiredAsterisk } from "./TextInputUtils";
import { focusOutline } from "./utils/focusOutlineStyle";
import { hexWithOpacityToRBGA } from "./utils/hexWithOpacityToRBGA";
type ItemMeta = {
  [key: string]: any;
};

export type Item = {
  label: React.ReactNode;
  value: string;
  icon?: string;
  subtext?: string;
  meta?: ItemMeta;
  indent?: number;
  disabled?: boolean;
};

export type GroupedItem = {
  name: string;
  items: Item[];
};

type SelectMenuContainerProps = {
  disabled?: boolean;
};

type TriggerProps = {
  invalid?: boolean;
  uneditable?: boolean;
  disabled?: boolean;
};

type OptionProps = {
  selected?: boolean;
};

export type SelectionProps = {
  selection?: Item;
  setSelection: (selection: Item) => void;
};

/**
 * @deprecated Use `<SelectMenuV2.Base compact ... />` instead.
 */
export type SelectMenuProps = {
  id?: string;
  className?: string;
  items: Item[] | GroupedItem[];
  description?: string;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  placeholder?: string;
  required?: boolean;
  touched?: boolean | FormikTouched<any>;
  uneditable?: boolean;
  renderInPortal?: boolean;
  sensitive?: boolean;
  mobileDropdownAlignment?: "fixed-bottom" | "fixed-stretch";
};

/**
 * @deprecated Use `<SelectMenuV2.Base compact ... />` instead.
 */
export const SelectMenuContainer = styled.div<SelectMenuContainerProps>`
  color: ${style.colors.grey.darkest};
  font-weight: ${style.vars.fontWeight.bold};
  position: relative;
  user-select: none;
  width: 100%;
  ${(props) =>
    props.disabled &&
    css`
      opacity: ${style.vars.disabledOpacity};
      pointer-events: none;
    `}
  ${mediaQueries.mediumMin} {
    width: ${style.vars.dropdown.width};
  }
`;

// Used to separate dropdown children from things such as
// the menu description or error message.
export const DropdownContainer = styled.div``;

export const Description = styled.div`
  font-size: ${style.vars.fontSize.small};
  margin-bottom: ${style.vars.whitespace.xsmall};
`;

export const OptionText = styled.div<{ $indent?: number }>`
  flex-grow: 1;
  overflow: hidden;
  padding-right: ${style.vars.whitespace.regular};
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ $indent }) =>
    $indent &&
    css`
      padding-left: ${$indent}rem;
    `}
  ~ ${IconSvg} {
    position: absolute;
    right: ${style.vars.whitespace.regular};
  }
`;

export const ErrorMessage = styled.div`
  color: ${style.colors.red.dark};
  font-size: ${style.vars.fontSize.small};
  position: absolute;
  font-weight: normal;
`;

export const CustomIcon = styled(Icon)`
  fill: ${style.colors.grey.darkest};
  margin-right: ${style.vars.whitespace.small};
`;

const ArrowIcon = styled(Icon)`
  font-size: ${style.vars.fontSize.small};
  right: ${style.vars.whitespace.regular};
`;

export const ErrorIcon = styled(Icon)`
  &&& {
    right: ${style.vars.space["6"]};
  }
`;

export const PlaceholderText = styled(OptionText)`
  color: ${style.colors.grey.dark};
  font-weight: ${style.vars.fontWeight.base};
`;

export const OptionSubtext = styled.div<OptionProps>`
  color: ${(props) => (props.selected ? style.colors.white : style.colors.grey.dark)};
  font-weight: ${style.vars.fontWeight.base};
  font-size: ${style.vars.fontSize.small};
  margin-top: -${style.vars.whitespace.xsmall};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MenuItem = styled.div`
  align-items: center;
  background: ${style.colors.white};
  cursor: pointer;
  display: flex;
  padding: 0 ${style.vars.whitespace.regular};
`;

const GroupTitle = styled.div`
  background: ${style.colors.white};
  position: sticky;
  top: -1px;
  padding: ${style.vars.whitespace.small} ${style.vars.whitespace.regular};
  color: ${style.colors.grey.dark};
  font-size: ${style.vars.fontSize.small};
  &:not(:first-child) {
    margin-top: ${style.vars.whitespace.regular};
  }
`;

export const Trigger = styled(MenuItem)<TriggerProps>`
  border: ${style.vars.borderWidth.thin} solid ${style.colors.blue.dark};
  border-radius: ${style.vars.borderRadius.base} ${style.vars.borderRadius.base} 0 0;
  min-height: ${style.vars.height.base};
  font-weight: ${style.vars.fontWeight.bold};
  position: relative;
  justify-content: space-between;
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.3;
      pointer-events: none;
    `}
  ${IconSvg} {
    color: ${style.colors.blue.dark};
  }
  &:active {
    background: ${style.colors.blue.lightest};
  }
  &:focus {
    ${focusOutline()}
  }
  ${(props) =>
    props.invalid &&
    css`
      ${OptionText},
      ${PlaceholderText} {
        padding-right: ${style.vars.space["7"]};
      }
    `};
  /*
   * This is to handle the situation when the menu is closed
   * (e.g. sharp corners become rounded corners).
   */
  &:last-of-type {
    border-color: ${(props) => (props.invalid ? style.colors.red.dark : style.colors.grey.dark)};
    border-radius: ${style.vars.borderRadius.base};
    ${IconSvg} {
      color: ${(props) => (props.invalid ? style.colors.red.dark : style.colors.grey.dark)};
    }
    ${(props) =>
      props.uneditable &&
      css`
        background-color: ${hexWithOpacityToRBGA(style.colors.white, style.vars.disabledOpacity)};
        border-color: ${hexWithOpacityToRBGA(style.colors.grey.dark, style.vars.disabledOpacity)};
        pointer-events: none;
        ${IconSvg} {
          color: ${hexWithOpacityToRBGA(style.colors.grey.dark, style.vars.disabledOpacity)};
        }
      `};
  }
  &:hover {
    border-color: ${(props) => (props.invalid ? style.colors.red.darkest : style.colors.blue.dark)};
    ${IconSvg} {
      color: ${(props) => (props.invalid ? style.colors.red.darkest : style.colors.blue.dark)};
    }
  }
`;

export type DropdownStyledProps = { $coords: CoordData; $shouldUseCoords: true } | { $shouldUseCoords?: false };

export const DropdownElement = styled(Group)<DropdownStyledProps>`
  background-color: ${style.colors.white};
  border: ${style.vars.borderWidth.thin} solid ${style.colors.blue.dark};
  border-bottom-left-radius: ${style.vars.borderRadius.base};
  border-bottom-right-radius: ${style.vars.borderRadius.base};
  border-top: none;
  width: 100%;
  ${(props) => {
    if (props.$shouldUseCoords) {
      return css`
        position: absolute;
        top: ${props.$coords.top}px;
        left: ${props.$coords.left}px;
        ${props.$coords.width ? `width: ${props.$coords.width}px;` : ""}
      `;
    }
  }}
  ${mediaQueries.mediumMin} {
    max-height: ${style.vars.dropdown.maxHeight};
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

type DropdownProps = {
  coords: CoordData;
  shouldUseCoords: boolean;
  updateCoords: () => void;
  children: React.ReactElement;
};

export const Dropdown: React.FC<DropdownProps> = ({ coords, shouldUseCoords = false, updateCoords, children }) => {
  const triggerCoordUpdate = _debounce(() => updateCoords(), 100);

  useEffect(() => {
    window.addEventListener("resize", triggerCoordUpdate);
    window.addEventListener("scroll", triggerCoordUpdate);

    return () => {
      window.removeEventListener("resize", triggerCoordUpdate);
      window.removeEventListener("scroll", triggerCoordUpdate);
    };
  });

  return cloneElement(children, {
    $coords: coords,
    $shouldUseCoords: shouldUseCoords,
  });
};

const RoverItem: React.FC<RoverProps> = ({ children, ...rest }) => {
  return (
    <Rover as={MenuItem} {...rest}>
      {children}
    </Rover>
  );
};

export const Option = styled(RoverItem)<OptionProps>`
  min-height: ${style.vars.height.base};
  position: relative;
  &:focus {
    outline: ${style.vars.borderWidth.base} dotted ${style.colors.grey.dark};
    outline-offset: -${style.vars.whitespace.xsmall};
  }
  &:hover {
    background: ${style.colors.grey.light};
  }
  &:active {
    background: ${style.colors.blue.lightest};
  }
  ${(props) =>
    props.selected &&
    css`
      background: ${style.colors.grey.dark};
      color: ${style.colors.white};
      ${IconSvg} {
        fill: ${style.colors.white};
      }
      &:focus {
        outline-color: ${style.colors.white};
      }
      &:hover,
      &:active {
        background: ${style.colors.grey.dark};
      }
    `};
`;

export const ListItemOption = styled(RoverItem)<OptionProps>`
  padding: 0px;
  &:focus {
    outline: ${style.vars.borderWidth.base} dotted ${style.colors.grey.dark};
    outline-offset: -${style.vars.whitespace.xsmall};
  }
  ${(props) =>
    props.selected &&
    css`
      &:focus {
        outline-color: ${style.colors.white};
      }
    `};
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      cursor: default;
      pointer-events: none;
      &:hover {
        color: initial;
      }
    `};
`;

const useUpdateSearchText = () => {
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchText("");
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchText]);

  return (keyPressed: string): string => {
    setSearchText(`${searchText}${keyPressed}`);
    return `${searchText}${keyPressed}`;
  };
};

const focusToElement = ({
  items,
  keyPressed,
  currentElementValue,
  itemRefs,
  updateSearchText,
}: {
  items: Item[];
  keyPressed: string;
  currentElementValue: string;
  itemRefs: React.RefObject<HTMLElement>[];
  updateSearchText: (keyPressed: string) => string;
}) => {
  const isKeyAlphaNumeric = !!(keyPressed.length === 1 && keyPressed.match(/^\w+$/));

  const itemStartsWithText = (item: Item, searchText: string): boolean =>
    !!(
      typeof item?.label === "string" &&
      (item.label as string)
        ?.toLowerCase()
        .normalize("NFD") //produce a form of the string that will be the same for all canonically equivalent strings
        .replace(/[\u0300-\u036f]/g, "") //remove combining diacritical marks
        .startsWith(searchText)
    );

  const indexToStart = (searchText: string): number => {
    const index = items.findIndex((el) => el.value === currentElementValue);
    // if next element doesn't start with the search text, reset index
    if (!itemStartsWithText(items[index + 1], searchText)) {
      return 0;
    }
    // if this element is the last one, reset index
    if (index + 1 === items.length) {
      return 0;
    }

    return index + 1;
  };

  if (isKeyAlphaNumeric) {
    const searchText = updateSearchText(keyPressed.toLowerCase());
    for (let itemIndex = indexToStart(searchText); itemIndex < items.length; itemIndex++) {
      if (itemStartsWithText(items[itemIndex], searchText)) {
        (itemRefs[itemIndex]?.current?.children[0] as HTMLElement)?.focus();
        break;
      }
    }
  }
};

/**
 * @deprecated Use `<SelectMenuV2.Base compact ... />` instead.
 */
export const SelectMenu = forwardRef<{ open: () => void; close: () => void }, SelectionProps & SelectMenuProps>(
  (
    {
      id,
      className,
      items,
      selection,
      description,
      disabled,
      error,
      errorMessage,
      placeholder,
      uneditable,
      setSelection,
      required,
      mobileDropdownAlignment = "fixed-bottom",
      renderInPortal = false,
      sensitive = false,
      ...inputProps
    },
    ref
  ) => {
    const { isMenuOpen, menuRef, onKeyUp, onKeyDown, onItemKeyUp, onClick, open, close, rover } = useKeyboardNav({ isMenuOpen: false });
    const { coords, updateCoords, shouldUseCoords } = usePortalDropdownCoords({
      menuRef,
      isMenuOpen,
      renderInPortal,
    });
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const invalid = error && !isMenuOpen;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const selectable = !disabled && !uneditable && isMenuOpen;
    const isSelected = (value: string) => {
      return selection && selection.value === value;
    };

    useImperativeHandle(
      ref,
      () => {
        return {
          open,
          close,
        };
      },
      [open, close]
    );

    const triggerSelect = (item: Item) => {
      close();
      if (selectable) {
        setSelection(item);
      }
    };

    const itemRefs: React.RefObject<HTMLDivElement>[] = React.useMemo(() => items.map(() => createRef()), [items.length]);
    const updateSearchText = useUpdateSearchText();

    const renderSelectMenuRow = (
      items: Item[],
      { label, value, subtext, icon, indent, disabled }: Item,
      index: number,
      name?: string
    ): React.ReactNode => {
      const shouldRenderGroupTitle = index === 0 && name;
      return (
        <div ref={itemRefs[index]} key={index}>
          {shouldRenderGroupTitle && <GroupTitle>{name}</GroupTitle>}
          <ListItemOption
            role="menuitem"
            selected={isSelected(value)}
            key={value}
            onClick={() => {
              triggerSelect(items[index]);
            }}
            onKeyDown={onKeyDown}
            onKeyUp={(e) => {
              focusToElement({
                items,
                keyPressed: e.key,
                currentElementValue: value,
                itemRefs,
                updateSearchText,
              });
              onItemKeyUp(e);
            }}
            data-value={value}
            disabled={disabled}
            {...rover}
          >
            <ListItem
              label={label as string}
              description={subtext}
              icon={icon}
              selected={isSelected(value)}
              selectIcon
              indent={indent}
            ></ListItem>
          </ListItemOption>
        </div>
      );
    };

    return (
      <SelectMenuContainer disabled={disabled} className={className} data-selected-value={selection && selection.value}>
        {description && (
          <Description>
            {description}
            {required && <RequiredAsterisk>*</RequiredAsterisk>}
          </Description>
        )}
        <DropdownContainer>
          <Trigger
            id={id}
            role="button"
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
            className={sensitive ? "fs-exclude" : ""}
            tabIndex={0}
            onClick={onClick}
            onKeyUp={onKeyUp}
            onKeyDown={onKeyDown}
            ref={menuRef}
            invalid={invalid}
            uneditable={uneditable}
            {...inputProps}
          >
            {selection ? (
              <OptionText>
                {selection.icon && <CustomIcon icon={selection.icon} />} {selection.label}{" "}
                {selection.subtext && <OptionSubtext selected={false}>{selection.subtext}</OptionSubtext>}
              </OptionText>
            ) : (
              <PlaceholderText>{placeholder}</PlaceholderText>
            )}
            {invalid && <ErrorIcon large icon="error" />}
            <ArrowIcon icon={isMenuOpen ? "arrow-up" : "arrow-down"} />
          </Trigger>
          <DropdownHandler
            mobileTitle={placeholder || "Select an option"}
            mobileAlignment={mobileDropdownAlignment}
            clickAway={(e) => {
              close();
              e.stopPropagation();
            }}
            inProp={isMenuOpen}
            renderInPortal={renderInPortal}
          >
            <Dropdown coords={coords} updateCoords={updateCoords} shouldUseCoords={shouldUseCoords}>
              <DropdownElement
                className={`dropdown-wrap${sensitive ? " fs-exclude" : ""}`}
                role="menu"
                aria-orientation="vertical"
                aria-label={description}
              >
                {(items as GroupedItem[]).map((item: GroupedItem, index) => {
                  if (item.items) {
                    return (
                      <div role="group" key={index}>
                        {(item.items as Item[]).map((innerItem, innerIndex) =>
                          renderSelectMenuRow(item.items, innerItem, Number(innerIndex), item.name)
                        )}
                      </div>
                    );
                  }

                  return renderSelectMenuRow(items as Item[], item as unknown as Item, Number(index));
                })}
              </DropdownElement>
            </Dropdown>
          </DropdownHandler>
        </DropdownContainer>
        {invalid && <ErrorMessage>{errorMessage ? errorMessage : "An error has occurred"}</ErrorMessage>}
      </SelectMenuContainer>
    );
  }
);
